import React, {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import {logDebug} from "../../utils/AppLog";
import {APP_GAME_AUDIO_URI} from "../../utils/services";

const GameAudio = forwardRef((props, ref) => {
  const {source, autoPlay, hasUserInteraction, loop, mute} = props;

  const refAudio = useRef();
  const canPlay = useRef(hasUserInteraction);
  const isPaused = useRef(false);
  const isPlayedOnce = useRef(false);
  const isMuted = useRef(mute); // use to play on first unmute
  const timeoutRefPause = useRef();

  useEffect(() => {
    let listener = null;
    if (refAudio.current) {
      refAudio.current.addEventListener("loadeddata", loadedListener);
      refAudio.current.addEventListener("error", loadErrorListener);
      refAudio.current.addEventListener("volumechange", volumeChangeListener);
      refAudio.current.addEventListener("play", playListener);

      if (!canPlay.current) {
        listener = () => {
          if (!canPlay.current) {
            canPlay.current = true;
            // playAuto();
          }
        };
        window.addEventListener('click', listener, {once: true});
      }
    }

    return () => {
      if (listener) {
        window.removeEventListener('click', listener);
      }
      pause();
      if (refAudio.current) {
        refAudio.current.removeEventListener("loadeddata", loadedListener);
        refAudio.current.removeEventListener("error", loadErrorListener);
        refAudio.current.removeEventListener("volumechange", volumeChangeListener);
        refAudio.current.removeEventListener("play", playListener);

        // refAudio.current.src = ''; // Unload resource
        // refAudio.current.load();
        // refAudio.current.remove();
        refAudio.current = null;
      }
      if (timeoutRefPause.current) {
        clearTimeout(timeoutRefPause.current);
        timeoutRefPause.current = null;
      }
    }
  }, []);

  useImperativeHandle(ref, () => ({
    play, pause
  }));

  const loadedListener = () => {
    logDebug('GameAudioLoaded', source);
    const audio = refAudio.current;
    if (audio) {
      playAuto();
    }
  };

  const loadErrorListener = () => {
    logDebug('GameAudioLoadFailed', source);
  };

  const volumeChangeListener = (event) => {
    const oldMuted = isMuted.current;
    isMuted.current = event.target.muted;
    if (!isMuted.current && oldMuted !== isMuted.current && !isPlayedOnce.current) {
      playAuto();
    }
  };

  const playListener = (event) => {
    if (!isPlayedOnce.current) {
      isPlayedOnce.current = !event.target.paused;
    }
    if (isPlayedOnce.current) {
      if (refAudio.current) {
        refAudio.current.removeEventListener("play", playListener);
      }
    }
  };

  const playAuto = () => {
    if (autoPlay && !isPaused.current) {
      play();
    }
  };

  const play = (onPlayed) => {
    logDebug('GameAudioPlayStart', source);
    isPaused.current = false;
    if (refAudio.current && canPlay.current) {
      // audio.muted = !canPlay.current;
      refAudio.current.currentTime = 0;
      refAudio.current.play().then(r => {
        logDebug('GameAudioPlayed', source);
        if (onPlayed) {
          onPlayed(1);
        }
      }).catch((error) => {
        logDebug('GameAudioPlayError ' + source, error);
      });
    }
  }

  const pause = (onPaused) => {
    logDebug('GameAudioPause', source);
    isPaused.current = true;
    if (refAudio.current && canPlay.current) {
      refAudio.current.pause();
      timeoutRefPause.current = setTimeout(() => {
        if (onPaused) {
          onPaused();
          clearTimeout(timeoutRefPause.current);
          timeoutRefPause.current = null;
        }
      });
    }
  }

  let url = source;
  if (!url.startsWith('https://') && !url.startsWith('blob:')) {
    url = APP_GAME_AUDIO_URI + url;
  }
  if (!source) {
    return null;
  }
  return (
    <audio
      ref={refAudio}
      src={url}
      autoPlay={false} // load resource before and then play if needed
      // muted={!canPlay || !autoPlay}
      muted={mute}
      // preload="auto"
      loop={loop}/>
  );
});

export default GameAudio;
